/**
 * Delivery offer hooks — TanStack Query hooks for the delivery fee discount
 * offers feature (عروض التوصيل). Follows the pattern established in
 * `useSliders.js` / `useVendors.js`, without a Redux sync since no other
 * component reads offers from the store.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getDeliveryOffers,
    createDeliveryOffer,
    updateDeliveryOffer,
    removeDeliveryOffer,
} from "services/deliveryOffers";

/** Query key factory — keeps cache keys consistent across all offer hooks. */
export const deliveryOfferKeys = {
    all: ["delivery-offers"],
};

/**
 * Fetches all delivery offers the current admin can see (the API scopes
 * non-super admins to their own city).
 */
export const useGetDeliveryOffers = () => {
    const query = useQuery({
        queryKey: deliveryOfferKeys.all,
        queryFn: async () => {
            const data = await getDeliveryOffers();
            return data.results;
        },
    });

    return {
        offers: query.data ?? [],
        loading: query.isLoading,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

/** Create a delivery offer and invalidate the offers cache. */
export const useCreateDeliveryOffer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createDeliveryOffer,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: deliveryOfferKeys.all });
        },
    });
};

/** Update a delivery offer (fields and/or vendor/area sets) and invalidate the cache. */
export const useEditDeliveryOffer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateDeliveryOffer,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: deliveryOfferKeys.all });
        },
    });
};

/** Delete a delivery offer and invalidate the cache. */
export const useDeleteDeliveryOffer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: removeDeliveryOffer,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: deliveryOfferKeys.all });
        },
    });
};
